import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../App";
import { logout, resetPassword } from "../../services/api";

const s = {
  page: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "calc(100vh - 64px)", padding: 24 },
  card: {
    background: "#2d8641", borderRadius: 8, padding: "36px 36px", width: "100%", maxWidth: 420,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)", display: "flex", flexDirection: "column", alignItems: "center",
    color: "#fff", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    boxSizing: "border-box",
  },
  logo: { maxWidth: "100%", height: "auto", marginBottom: 24 },
  title: { fontSize: 20, fontWeight: "bold", margin: "0 0 20px 0", textAlign: "center" },
  input: {
    width: "100%", padding: "14px 16px", border: "none", borderRadius: 8, fontSize: 15,
    outline: "none", marginBottom: 16, boxSizing: "border-box", color: "#333", background: "#fff",
  },
  btn: {
    width: "100%", padding: "14px", background: "#fabd05", color: "#000", border: "none",
    borderRadius: 8, fontSize: 16, fontWeight: "bold", cursor: "pointer", transition: "background 0.2s",
  },
  btnDisabled: { opacity: 0.6, cursor: "not-allowed" },
  error: {
    background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px",
    fontSize: 13, color: "#dc2626", marginBottom: 18, width: "100%", boxSizing: "border-box", textAlign: "center",
  },
  success: {
    background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 14px",
    fontSize: 13, color: "#15803d", marginBottom: 18, width: "100%", boxSizing: "border-box", textAlign: "center",
  },
  loginLink: { color: "#fff", fontWeight: "bold", marginTop: 20, textAlign: "center" },
};

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logoutUser } = useAuth();
  const token = searchParams.get("token");

  useEffect(() => {
    let mounted = true;
    logout()
      .catch(() => {})
      .finally(() => {
        logoutUser();
        if (mounted) setSessionReady(true);
      });
    return () => { mounted = false; };
  }, [logoutUser]);

  if (!token) {
    return (
      <div style={s.page}>
        <div style={s.card} className="animate-fade-in-up">
          <img src="/images/logo.png" alt="SPEEDiagnostix" style={s.logo} />
          <p style={{ textAlign: "center" }}>This reset link is invalid or has expired.</p>
          <Link to="/login?switchAccount=1" style={s.loginLink}>Go to the login screen</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate("/login?switchAccount=1", { replace: true }), 1800);
    } catch (err) {
      setError(err.response?.data?.error || "Reset failed. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <form style={s.card} onSubmit={handleSubmit} className="animate-fade-in-up">
        <img src="/images/logo.png" alt="SPEEDiagnostix" style={s.logo} />
        <h1 style={s.title}>Set a new password</h1>
        {error && <div style={s.error}>{error}</div>}
        {success && <div style={s.success}>Password updated! Redirecting to login…</div>}
        {!success && (
          <>
            <input
              style={s.input}
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="New password"
              required
              minLength={8}
            />
            <input
              style={{ ...s.input, marginBottom: 24 }}
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              required
            />
            <button
              type="submit"
              style={{ ...s.btn, ...((loading || !sessionReady) ? s.btnDisabled : {}) }}
              disabled={loading || !sessionReady}
              onMouseEnter={e => { if (!loading) e.target.style.background = "#e6ad03"; }}
              onMouseLeave={e => { e.target.style.background = "#fabd05"; }}
            >
              {!sessionReady ? "Ending Previous Session…" : loading ? "Updating…" : "Update Password"}
            </button>
          </>
        )}
        <Link to="/login?switchAccount=1" style={s.loginLink}>Already have a password? Go to login</Link>
      </form>
    </div>
  );
}
